import type { IPasskey, TAuthMethod } from '~/types/passkey/models'

// State behind the passkey mocks (cynque-api-server#1183's routes).
//
// It lives in localStorage, not module memory, because MSW's handlers run in the
// page: a full navigation (an e2e `page.goto`, a sign-out to /Login) re-imports
// this module, and a passkey registered before it would vanish. localStorage
// survives that within one browser context and is fresh in the next, which is
// the lifetime a spec wants. "Start Over" does not reset it.
//
// The mock cannot check signatures. What it does check is everything the
// webapp controls: the ceremony type and origin inside `clientDataJSON`, a
// challenge this mock issued for that purpose and has not spent, and — for
// sign-in — a credential it registered and the user handle it gave out.

/** e2e writes this (via addInitScript) to choose the deployment's sign-in methods. */
export const AUTH_METHODS_KEY = 'mock:authMethods'
/** The RP ID the mock answers with, when set; else the page's own host. */
export const RP_ID_KEY = 'mock:passkeyRpId'
const PASSKEYS_KEY = 'mock:passkeys'
const CHALLENGES_KEY = 'mock:passkeyChallenges'

const DEFAULT_AUTH_METHODS: TAuthMethod[] = ['passkey', 'password']
const CHALLENGE_TTL_MS = 300_000

/**
 * `user.id` for the mock's signed-in user (XXU0005): the api uses the user's
 * UUID bytes. Any fixed 16 bytes will do, base64url-encoded.
 */
export const MOCK_USER_HANDLE = bytesToBase64Url(
  new Uint8Array([
    0xb1, 0xf3, 0xc6, 0xa2, 0x5e, 0x0d, 0x4c, 0x4b, 0x9a, 0x77, 0, 0, 0, 0, 0,
    0x05,
  ])
)
export const MOCK_USER_EMAIL = 'merryl@wxtechnologygroup.com'

export interface IMockPasskey extends IPasskey {
  credentialId: string
}

type TPurpose = 'authentication' | 'registration'

interface IMockChallenge {
  challenge: string
  purpose: TPurpose
  issuedAt: number
}

function storage(): Storage | undefined {
  return typeof localStorage === 'undefined' ? undefined : localStorage
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = storage()?.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  storage()?.setItem(key, JSON.stringify(value))
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64UrlToString(value: string): string {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
  return new TextDecoder().decode(
    Uint8Array.from(binary, (c) => c.charCodeAt(0))
  )
}

// -- methods ----------------------------------------------------------------------

export function readAuthMethods(): TAuthMethod[] {
  const methods = readJson<unknown>(AUTH_METHODS_KEY, DEFAULT_AUTH_METHODS)
  return Array.isArray(methods)
    ? (methods as TAuthMethod[])
    : DEFAULT_AUTH_METHODS
}

/**
 * The RP ID: the real api's is `host(WEBAPP_ORIGIN)`, which the page's own host
 * stands in for. e2e sets `mock:passkeyRpId` to play the page being opened at
 * an address that RP ID doesn't cover (the app's Heroku address), which the
 * page's own host can never produce.
 */
export function readRpId(): string {
  const rpId = readJson<unknown>(RP_ID_KEY, null)
  return typeof rpId === 'string' ? rpId : globalThis.location.hostname
}

// -- passkeys -----------------------------------------------------------------------

export function readPasskeys(): IMockPasskey[] {
  return readJson<IMockPasskey[]>(PASSKEYS_KEY, [])
}

export function writePasskeys(passkeys: IMockPasskey[]) {
  writeJson(PASSKEYS_KEY, passkeys)
}

/** What the api returns for a passkey: never the credential id. */
export function toPublicPasskey({
  credentialId,
  ...rest
}: IMockPasskey): IPasskey {
  return rest
}

// -- challenges ---------------------------------------------------------------------

export function issueChallenge(purpose: TPurpose): string {
  const challenge = bytesToBase64Url(crypto.getRandomValues(new Uint8Array(32)))
  const now = Date.now()
  const live = readJson<IMockChallenge[]>(CHALLENGES_KEY, []).filter(
    (c) => now - c.issuedAt < CHALLENGE_TTL_MS
  )
  writeJson(CHALLENGES_KEY, [...live, { challenge, purpose, issuedAt: now }])
  return challenge
}

/**
 * Check a ceremony's `clientDataJSON` and spend its challenge. Like the api, the
 * challenge is spent even when the check then fails, so a retry needs new options.
 */
export function consumeCeremony(
  clientDataJSON: unknown,
  purpose: TPurpose
): boolean {
  if (typeof clientDataJSON !== 'string') {
    return false
  }
  let clientData: { type?: string; challenge?: string; origin?: string }
  try {
    clientData = JSON.parse(base64UrlToString(clientDataJSON))
  } catch {
    return false
  }
  const challenges = readJson<IMockChallenge[]>(CHALLENGES_KEY, [])
  const match = challenges.find((c) => c.challenge === clientData.challenge)
  if (match) {
    writeJson(
      CHALLENGES_KEY,
      challenges.filter((c) => c !== match && c.challenge !== match.challenge)
    )
  }
  const expectedType =
    purpose === 'registration' ? 'webauthn.create' : 'webauthn.get'
  return (
    !!match &&
    match.purpose === purpose &&
    Date.now() - match.issuedAt < CHALLENGE_TTL_MS &&
    clientData.type === expectedType &&
    clientData.origin === globalThis.location?.origin
  )
}
